/**
 * downloadDocx — deliver an authenticated .docx WITHOUT navigating.
 *
 * Background (2026-06-23 device-verified fix): the AI Tools download endpoints
 * are authenticated GETs that return a binary .docx
 * (`Content-Disposition: attachment`). Delivering them by NAVIGATION (an
 * `<a href>` / `window.open`) dead-ends in iOS in-app browsers — a new browser
 * context opens on the auth'd binary endpoint, renders blank, its native share
 * shares the page URL (not the file), and its buttons orphan. A
 * `target="_blank"` hotfix shipped first and FAILED on device for exactly this
 * reason: navigating ANY browser context to an authenticated attachment
 * endpoint dead-ends there.
 *
 * This rides the app's normal authenticated-fetch path instead:
 *   1. `fetch(url)` — same-origin, so the SWA auth cookie is sent (the default
 *      `credentials: 'same-origin'`, matching api.ts) and the binary actually
 *      loads. No navigation, no auth-bounce, no blank context.
 *   2. Hand the resulting `File` to the OS share sheet (`navigator.share` with
 *      files) on capable browsers — the share sheet is OS-level so it works
 *      inside in-app browsers and carries the REAL file (Save to Files / Open
 *      in Word / Message), returning to the app cleanly.
 *   3. Fall back to a transient objectURL `<a download>` (a true download, no
 *      navigation) on desktop / where Web Share with files is unsupported.
 *
 * Throws on a failed fetch (or an auth-bounce HTML body) so callers surface an
 * in-app error — never the silent blank screen of the old approach.
 */

import { writeFetch } from './writeFetch';
import { apiErrorFrom } from './api';

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Parse the filename out of a `Content-Disposition` header. Honors the RFC 5987
 * `filename*=UTF-8''…` extended form first, then the plain `filename="…"`.
 * Returns null when absent/unparseable so the caller can apply a fallback.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extended?.[1]) {
    const raw = extended[1].trim().replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

type ShareCapableNavigator = Navigator & {
  canShare?: (data?: { files?: File[] }) => boolean;
  share?: (data?: { files?: File[]; title?: string }) => Promise<void>;
};

export async function downloadDocx(url: string, fallbackName: string): Promise<void> {
  return deliverFile(await fetchDocx(url, fallbackName));
}

/**
 * POST-body variant: some downloads (the stateless Medication List) send the
 * reviewed document in the request body rather than referencing a saved draft
 * by id. Same same-origin credentials + same OS share / objectURL delivery as
 * the GET path; only the fetch differs. Throws on a failed fetch so callers
 * surface an in-app error (never the silent blank screen).
 */
export async function downloadDocxPost(
  url: string,
  body: unknown,
  fallbackName: string,
): Promise<void> {
  return deliverFile(await fetchDocx(url, fallbackName, body));
}

/**
 * The Word file a download would deliver, as a File, without delivering it:
 * the same request Download Word makes (GET, or POST with `body`). The PDF
 * and Print buttons start here, so they read exactly what Word gets.
 */
export async function fetchDocx(url: string, fallbackName: string, body?: unknown): Promise<File> {
  const res =
    body === undefined
      ? await fetch(url)
      : await writeFetch(url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
  if (!res.ok) throw await apiErrorFrom(res, 'Download');
  const blob = await res.blob();
  // An auth-bounce returns an HTML login page with a 200 — treat that as an
  // error rather than "downloading" the login page.
  if (blob.type.includes('text/html')) {
    throw new Error('Download failed: not signed in');
  }
  const filename =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallbackName;
  return new File([blob], filename, { type: blob.type || DOCX_MIME });
}

/** Shared delivery: share-sheet or objectURL the file. */
export async function deliverFile(file: File): Promise<void> {
  const filename = file.name;
  // Share-capable (iOS Safari / installed PWA / many in-app browsers): hand the
  // actual file to the OS share sheet.
  const nav = navigator as ShareCapableNavigator;
  if (nav.canShare?.({ files: [file] }) && nav.share) {
    try {
      await nav.share({ files: [file], title: filename });
      return;
    } catch (e) {
      // User dismissed the share sheet — done, not an error.
      if (e instanceof DOMException && e.name === 'AbortError') return;
      // Anything else (share failed): fall through to the objectURL download.
    }
  }

  // Desktop / fallback: a true download via a transient object URL (no nav).
  const objectUrl = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
